import React, {useEffect, useMemo, useRef, useState} from "react";
import clsx from "clsx";
import {useController, useForm} from "react-hook-form";
import {Box, Button, Tooltip, useTheme} from "@material-ui/core";
import LockIcon from "@material-ui/icons/Lock";
import MenuBookOutlinedIcon from "@material-ui/icons/MenuBookOutlined";
import Form, {
  ActionButton,
  Actions,
  BasicInput,
  BasicSelect,
} from "view/components/Form";
import caseInsensitiveSort from "tools/common/caseInsensitiveSort";
import {useCurrentShowBuildTypes} from "hooks";
import useShowCustomFields from "hooks/useShowCustomFields";
import useRichTextIntegrationControls from "hooks/show/useRichTextIntegrationControls";
import {TooltipVideoContainer} from "view/components/RichTextEditor/components/TooltipVideoContainer";
import RichTextEditor from "view/components/RichTextEditor";
import {
  releasePendingImagesIn,
  storageServiceImageAdapter,
} from "view/components/RichTextEditor/adapters";
import termUrlMatcher from "view/components/RichTextEditor/matchers/termUrlMatcher";
import FfairModal from "view/components/modal/FfairModal";
import {
  IBaseTerm,
  ICategory,
  IGlossary,
  ITerm,
  TranslatableEntity,
} from "@evennt-tech/api-v2-core";
import {parseTextFromHTML} from "tools/file/parseTextFromHTML";
import Alert from "view/components/Alert";
import {trimValidationPattern} from "tools/validations";
import {RichTextDefaultVariableSetName} from "view/components/RichTextEditor/constants/Variables";
import {useLocalisation} from "hooks/translation";
import {LocalisationTarget} from "ts/app/translation";
import {
  LocalisationProvider,
  LocalisationSection,
  LocalisationSettingsBar,
} from "view/components/Localisation";
import DotsMenu from "view/components/DotsMenu";
import GlossaryTermDescription from "view/components/GlossaryTerm/Description";
import {isLinkedTerm} from "api/showInfoLibrary/types";
import {useLibraryLinkStyles, VIDEO_TOOLTIP_BOTTOM} from "./styles";

type Props = {
  isOpen: boolean;
  showId: string;
  glossary: IGlossary;
  categories: ICategory[];
  terms: ITerm[];
  onClose: () => void;
  onSave: (
    term: IBaseTerm & {id: string; buildTypes: string[]}
  ) => Promise<string | undefined | void> | void;
  editableTerm?: ITerm;
  /*
   * The three library actions, each rendered only when supplied — that is how
   * the Organiser-Admin gate reaches this form without an auth dependency.
   */
  /** `ADD TO LIBRARY`, in the local section's banner. */
  onAddToLibrary?: (term: ITerm) => void;
  /** `Unlink from library`, in the linked section's banner menu. */
  onUnlinkFromLibrary?: (term: ITerm) => void;
  /** `Remove`, in the same menu. */
  onRemove?: (term: ITerm) => void;
};

/* On every locked field — there is no label saying it any more. */
const LOCKED_FIELD_HINT =
  "The element is managed in the library and can be edited by the Organiser Admin.";

const RICH_TEXT_VARIABLE_SET_NAMES: RichTextDefaultVariableSetName[] = [
  RichTextDefaultVariableSetName.show,
  RichTextDefaultVariableSetName.onsiteDetails,
];

const GlossaryTermFormModal: React.FC<Props> = ({
  showId,
  glossary,
  onSave,
  isOpen,
  onClose,
  editableTerm,
  categories,
  terms,
  onAddToLibrary,
  onUnlinkFromLibrary,
  onRemove,
}) => {
  const theme = useTheme();
  const libraryClasses = useLibraryLinkStyles();

  /*
   * A linked section's `title` and `description` are the library's, so both are
   * read-only (AC 4); `Build type`, `Category`, pin and pop-up stay this
   * Show's. No `Internal name` on either variant — it is a Library-level
   * concept (Q14), asked for once in the push `Duplicate warning`.
   */
  const isLinked = !!editableTerm && isLinkedTerm(editableTerm);
  const form = useForm<IBaseTerm & {id: string; buildTypes: string[]}>({
    mode: "all",
    defaultValues: {
      ...editableTerm,
      description: editableTerm?.description || "",
      buildTypes:
        editableTerm?.termToBuilders?.map(({builderId}) => builderId) || [],
    },
  });
  const {setValue, register, reset} = form;

  const [termError, setTermError] = useState(false);
  const [isLoadingValidation, setIsLoadingValidation] = useState(true);

  // Memoize the rules so RHF doesn't see a new options object identity on
  // every render.
  const descriptionRules = useMemo(
    () => ({
      required: true,
      validate: (value: string) => {
        if (
          value.includes("img") ||
          value.includes("video") ||
          value.includes("iframe")
        ) {
          return true;
        }
        if (
          parseTextFromHTML(value).trim().length === 0 &&
          value?.length !== 0
        ) {
          return false;
        }
        return true;
      },
    }),
    []
  );

  // RHF v7's canonical pattern for headless fields (no <input>) is
  // useController, called as a top-level hook. Do NOT pass a defaultValue
  // here — useForm above already initialised description via defaultValues
  // and passing one again into useController causes RHF to take a different
  // internal subscription branch on first vs subsequent render (= "change
  // in the order of Hooks" warning at the very last hook position).
  useController({
    name: "description",
    control: form.control,
    rules: descriptionRules,
  });

  // Abandoning the form must free any image the user pasted/inserted but never
  // saved: the pending-images registry deliberately outlives the editor
  // (wizards, modals), so nothing else would ever revoke those object URLs and
  // the Blobs would stay in memory for the lifetime of the tab.
  const hasSubmittedRef = useRef(false);
  const handleClose = () => {
    if (!hasSubmittedRef.current) {
      releasePendingImagesIn(form.getValues("description"));
    }
    onClose();
  };

  const [buildTypes] = useCurrentShowBuildTypes();
  // The task editor's two sources, so a section offers the same set.
  const [customFields] = useShowCustomFields({localised: true});
  const integrationControls = useRichTextIntegrationControls({showId});

  const buildTypesOptions = useMemo(() => {
    if (!buildTypes) {
      return [];
    }

    if (!buildTypes.length) {
      return [];
    }

    return buildTypes?.map((buildType) => ({
      value: buildType.id,
      label: buildType.name,
    }));
  }, [buildTypes]);
  const categoryOptions = useMemo(
    () =>
      caseInsensitiveSort<ICategory>(categories, "title").map(
        ({title, id}) => ({
          label: title,
          value: id,
        })
      ),
    [categories]
  );

  useEffect(() => {
    if (isOpen && !editableTerm) {
      setValue(
        "buildTypes",
        buildTypes?.map(({id}) => id)
      );
    }
  }, [isOpen, buildTypesOptions, setValue, editableTerm]);

  const localisationTargets = useMemo<LocalisationTarget[]>(
    () => [
      {
        key: "title",
        entity: TranslatableEntity.TERM,
        entityId: editableTerm?.id,
        field: "title",
        label: "Title",
        getSourceText: () => form.getValues("title") ?? "",
      },
      {
        key: "description",
        entity: TranslatableEntity.TERM,
        entityId: editableTerm?.id,
        field: "description",
        label: "Text",
        kind: "richText",
        getSourceText: () => form.getValues("description") ?? "",
      },
    ],
    [editableTerm?.id, form]
  );

  const localisation = useLocalisation({
    showId,
    targets: localisationTargets,
    enabled: isOpen,
  });

  const onSubmit = async (
    data: IBaseTerm & {id: string; buildTypes: string[]}
  ) => {
    const {
      id,
      termToBuilders,
      categoryId,
      title,
      description,
      buildTypes,
    } = data;

    // From here on the pending images belong to the save: `onSaveTerm` uploads
    // them and releases them itself once the term persisted.
    hasSubmittedRef.current = true;

    const payload = {
      id,
      termToBuilders,
      title,
      description,
      categoryId,
      buildTypes,
    };

    if (editableTerm?.id) {
      await localisation.save();
      await onSave(payload);
      return;
    }

    const createdId = await onSave(payload);
    await localisation.save({
      resolveEntityId: () =>
        typeof createdId === "string" ? createdId : undefined,
    });
    onClose();
  };

  const onChangeDescription = (description: string) => {
    setValue("description", description, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const validateTitleNotUsed = (title: string): boolean => {
    setIsLoadingValidation(true);

    try {
      if (!title) return false;

      const sameTitle = terms.filter((term) => {
        if (editableTerm) {
          return (
            term.title.toLowerCase() === title.trim().toLowerCase() &&
            term.id !== editableTerm.id
          );
        }
        return term.title.toLowerCase() === title.trim().toLowerCase();
      });

      setTermError(sameTitle.length >= 1);

      return sameTitle.length === 0;
    } catch (e) {
      console.error(e);
      return false;
    } finally {
      setIsLoadingValidation(false);
    }
  };

  /**
   * The banner carries the library relationship and the actions that change it.
   * The `⋮` sits here rather than on the card because both its items act on the
   * link, and both are Organiser Admin only.
   */
  const renderLibraryBanner = () => {
    if (!editableTerm) return null;

    if (isLinked) {
      return (
        <Alert
          severity="info"
          fullWidth
          className={clsx(libraryClasses.banner, libraryClasses.linkedBanner)}
          customIcon={
            <MenuBookOutlinedIcon className={libraryClasses.bannerIcon} />
          }
        >
          <Box className={libraryClasses.bannerRow}>
            <span className={libraryClasses.bannerText}>
              This is a Library Section
            </span>
            {(onUnlinkFromLibrary || onRemove) && (
              <DotsMenu
                id="linked-show-info-section-actions"
                options={[
                  ...(onUnlinkFromLibrary
                    ? [
                        {
                          node: <Box>Unlink from library</Box>,
                          onClick: () => onUnlinkFromLibrary(editableTerm),
                          divide: !!onRemove,
                        },
                      ]
                    : []),
                  ...(onRemove
                    ? [
                        {
                          node: (
                            <Box color={theme.palette.error.main}>Remove</Box>
                          ),
                          onClick: () => onRemove(editableTerm),
                        },
                      ]
                    : []),
                ]}
              />
            )}
          </Box>
        </Alert>
      );
    }

    return (
      <Alert
        severity="info"
        fullWidth
        className={clsx(libraryClasses.banner, libraryClasses.localBanner)}
        // No leading icon here — the book rides with the action instead.
        icon={false}
      >
        <Box className={libraryClasses.bannerRow}>
          <span className={libraryClasses.bannerText}>
            Local Section (not added to the library)
          </span>
          {onAddToLibrary && (
            <Button
              color="primary"
              className={libraryClasses.addToLibrary}
              endIcon={<MenuBookOutlinedIcon />}
              onClick={() => onAddToLibrary(editableTerm)}
            >
              ADD TO LIBRARY
            </Button>
          )}
        </Box>
      </Alert>
    );
  };

  const formTitle = `${editableTerm ? "Edit" : "Add"} Section`;
  const matchers = useMemo(
    () => [termUrlMatcher(showId, terms, theme.palette.error.main)],
    [showId, terms, theme.palette.error.main]
  );

  useEffect(() => {
    setTermError(false);
  }, [isOpen]);

  return (
    <FfairModal onClose={handleClose} open={isOpen} reset={reset}>
      <Form
        onSubmit={form.handleSubmit(onSubmit)}
        form={form}
        style={{width: "640px", position: "relative"}}
        title={formTitle}
      >
        <LocalisationProvider controller={localisation}>
          {renderLibraryBanner()}
          <LocalisationSettingsBar />
          <BasicInput
            label="Title"
            name="title"
            disabled={isLinked}
            {...(isLinked && {
              InputProps: {
                readOnly: true,
                endAdornment: (
                  <Tooltip title={LOCKED_FIELD_HINT}>
                    <LockIcon className={libraryClasses.lockIcon} />
                  </Tooltip>
                ),
              },
            })}
            rules={{
              required: true,
              minLength: 1,
              pattern: trimValidationPattern,
              ...(!isLinked && {validate: validateTitleNotUsed}),
            }}
            onBlur={() => {
              setTermError(false);
            }}
          />

          {termError && (
            <Alert style={{marginBottom: "24px"}} severity="error">
              The entered title already exists. Please check your entry.
            </Alert>
          )}

          <LocalisationSection
            id="glossary-term-title"
            targetKeys={["title"]}
            readOnly={isLinked}
          />

          {/*
            Hidden on a linked section, per the design. `categoryId` still travels
            with the form (it comes from the term's own defaults), so saving
            cannot blank it — the category link is simply not something this form
            is the place to change.
          */}
          {!isLinked && (
            <BasicSelect
              label="Category"
              name="categoryId"
              options={categoryOptions}
            />
          )}

          <BasicSelect
            label="Build type"
            name="buildTypes"
            options={buildTypesOptions}
            selectStyle={{
              marginBottom: 24,
              width: "100%",
            }}
            multiple
            selectAll
            required={false}
          />

          <TooltipVideoContainer bottomPosition={VIDEO_TOOLTIP_BOTTOM}>
            {isLinked ? (
              /*
               * The published renderer, not a disabled editor — so this is
               * exactly what an exhibitor reads.
               */
              <Box className={libraryClasses.lockedField}>
                <Box className={libraryClasses.lockedBody}>
                  <Box className={libraryClasses.lockedBodyContent}>
                    <GlossaryTermDescription
                      description={editableTerm?.description ?? ""}
                    />
                  </Box>
                  <Tooltip title={LOCKED_FIELD_HINT}>
                    <LockIcon className={libraryClasses.lockIcon} />
                  </Tooltip>
                </Box>
              </Box>
            ) : (
              <RichTextEditor
                id="description"
                label="Text *"
                controls={[
                  "image",
                  "video",
                  "variables",
                  "iframe",
                  ...integrationControls,
                ]}
                matchers={matchers}
                imageAdapter={storageServiceImageAdapter}
                onChange={onChangeDescription}
                defaultValue={editableTerm?.description}
                externalRedirectShowId={showId}
                showCustomFields={customFields}
                variableSets={RICH_TEXT_VARIABLE_SET_NAMES}
              />
            )}

            <LocalisationSection
              id="glossary-term-description"
              targetKeys={["description"]}
              withTopMargin={!isLinked}
              disableBottomMargin
              readOnly={isLinked}
              richTextOptions={{
                controls: [
                  "image",
                  "video",
                  "variables",
                  "iframe",
                  ...integrationControls,
                ],
                matchers,
                imagePath: "glossary",
                externalRedirectShowId: showId,
                showCustomFields: customFields,
                variableSets: RICH_TEXT_VARIABLE_SET_NAMES,
              }}
            />

            <Actions>
              <ActionButton
                onClick={handleClose}
                text="Cancel"
                name="cy-button-cancel-glossary-term-form"
              />
              <ActionButton
                type="submit"
                /*
                 * `isLoadingValidation` starts `true` and only
                 * `validateTitleNotUsed` clears it — which a linked section
                 * never runs, leaving Save disabled for good (AC 5).
                 */
                disabled={(!isLinked && isLoadingValidation) || termError}
                text="Save"
                name="cy-button-save-glossary-term"
                allowClean={localisation.isDirty}
              />
            </Actions>
          </TooltipVideoContainer>
        </LocalisationProvider>
      </Form>
    </FfairModal>
  );
};

export default GlossaryTermFormModal;
